import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { IContract } from '@components/DeviceActionModal/types';
import useSwr from '@hooks/useSwr';
import SessionContext from '@providers/Session/SessionContext';
import api from '@services/api';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Modal,
  Row,
  Select,
  message,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { CopyOutlined } from '@ant-design/icons';
import moment, { Moment } from 'moment';
import SelectCustomers from '@components/SelectCustomers';
import rolesConstants, { RolesTypes } from '@utils/rolesConstants';

type ReportTypes = 1 | 2 | 3;

interface RequestReportFormValues {
  period: Moment;
  customerId?: number;
  type: ReportTypes;
  assetNumber: string[];
}

const ConsumptionAnalysisReport = () => {
  const { t } = useTranslation('reports');
  const { selectedApp, theme, isRoot, effectiveRoles } =
    useContext(SessionContext);

  const hasRole = useCallback(
    (role: RolesTypes) => effectiveRoles?.portal?.includes(role),
    [effectiveRoles],
  );

  const [form] = Form.useForm<RequestReportFormValues>();
  const customerId = Form.useWatch('customerId', form);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isResourceNotFoundError, setIsResourceNotFoundError] = useState(false);

  const { data: contracts } = useSwr<IContract[] | ''>(
    openModal ? '/service-proxy/contracts' : null,
    {
      params: {
        product: selectedApp?.toUpperCase(),
        customerId,
      },
    },
  );

  const contractsOptions = useMemo(
    () =>
      Array.isArray(contracts)
        ? contracts?.map(({ planName, assetNumber, nickname }) => ({
            value: assetNumber,
            label: `${assetNumber}${
              nickname ? ` - ${nickname}` : ''
            } - ${planName}`,
          }))
        : [],
    [contracts],
  );

  const handleClose = useCallback(() => {
    setOpenModal(false);
    setIsResourceNotFoundError(false);
    form.resetFields();
  }, [form]);

  const requestReport = useCallback(
    async (values: RequestReportFormValues) => {
      try {
        setLoading(true);

        const payload = {
          ...values,
          period: values?.period?.format('YYYY-MM'),
        };

        await api.post('/service-proxy/opened/invoices/report', payload);

        message.success(t('consumption-analysis-report.messages.success'));
        handleClose();
      } catch (error) {
        const errorCode =
          // @ts-ignore
          error?.response?.data?.errorCode;
        setIsResourceNotFoundError(errorCode === 'RESOURCE_NOT_FOUND');
        message.error(t('consumption-analysis-report.messages.error'));
      } finally {
        setLoading(false);
      }
    },
    [handleClose, t],
  );

  const typeOptions = useMemo(
    () =>
      [
        {
          label: t('consumption-analysis-report.types.data-consumption'),
          value: 1,
          role: rolesConstants.REQUEST_CONSUMPTION_REPORT,
        },
        {
          label: t('consumption-analysis-report.types.consumption-by-location'),
          value: 2,
          role: rolesConstants.REQUEST_LOCATION_REPORT,
        },
        {
          label: t(
            'consumption-analysis-report.types.data-consumption-and-by-location',
          ),
          value: 3,
          role: rolesConstants.REQUEST_CONSUMPTION_LOCATION_REPORT,
        },
      ].filter(item => hasRole(item.role)),
    [hasRole, t],
  );

  useEffect(() => {
    if (typeOptions?.length === 1) {
      form.setFieldValue('type', typeOptions[0].value);
    }
  }, [form, typeOptions]);

  const renderContract = (
    <Col span={24}>
      <Form.Item
        label={t('consumption-analysis-report.fields.contracts')}
        name="assetNumber"
        rules={[
          {
            required: true,
            message: t('request.fieldRequired'),
          },
        ]}
        help={
          isResourceNotFoundError ? (
            <span style={{ color: theme.colors.red }}>
              {t('consumption-analysis-report.messages.no-consumption-found')}
            </span>
          ) : undefined
        }
        style={{ marginBottom: isRoot ? 8 : 0 }}
      >
        <Select
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="label"
          options={contractsOptions}
          loading={contracts === undefined}
          disabled={
            contracts === '' ||
            contractsOptions.length === 0 ||
            (isRoot && !customerId)
          }
        />
      </Form.Item>
    </Col>
  );

  if (typeOptions.length === 0) {
    return null;
  }

  return (
    <Row style={{ marginBottom: 16 }}>
      <Modal
        title={t('consumption-analysis-report.title')}
        open={openModal}
        onOk={() => form.submit()}
        onCancel={handleClose}
        confirmLoading={loading}
        okText={t('consumption-analysis-report.actions.request')}
        cancelText={t('consumption-analysis-report.actions.cancel')}
        bodyStyle={{
          padding: '40px 48px',
        }}
        width={480}
      >
        <Form<RequestReportFormValues>
          form={form}
          layout="vertical"
          onFinish={requestReport}
          initialValues={{
            period: moment(),
          }}
        >
          <Row gutter={[16, 0]}>
            <Col span={24}>
              <Form.Item
                label={t('consumption-analysis-report.fields.period')}
                name="period"
              >
                <DatePicker
                  format="MMM / YYYY"
                  picker="month"
                  allowClear={false}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label={t('consumption-analysis-report.fields.type')}
                name="type"
                rules={[
                  {
                    required: true,
                    message: t('request.fieldRequired'),
                  },
                ]}
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={typeOptions}
                  disabled={typeOptions?.length === 1}
                />
              </Form.Item>
            </Col>
            {isRoot ? (
              <Card
                style={{
                  marginTop: 8,
                  width: '100%',
                  background: theme?.colors?.background1,
                }}
              >
                <Col span={24}>
                  <Form.Item
                    label={t('attributes:customer')}
                    name="customerId"
                    rules={[
                      {
                        required: true,
                        message: t('request.fieldRequired'),
                      },
                    ]}
                  >
                    <SelectCustomers
                      onChange={() => {
                        form.setFieldValue('assetNumber', undefined);
                      }}
                    />
                  </Form.Item>
                </Col>
                {renderContract}
              </Card>
            ) : (
              renderContract
            )}
          </Row>
        </Form>
      </Modal>
      <Button icon={<CopyOutlined />} onClick={() => setOpenModal(true)}>
        {t('consumption-analysis-report.title')}
      </Button>
    </Row>
  );
};

export default ConsumptionAnalysisReport;
